import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  Megaphone,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Link2,
} from 'lucide-react';
import { adminApi } from '../services/api';

const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const webhookUrl =
  (apiBase.endsWith('/api') ? apiBase : apiBase + '/api') + '/webhooks/meta/leadgen';

interface MetaLeadPage {
  id: string;
  metaPageId: string;
  pageName: string | null;
  isSubscribed: boolean;
  createdAt: string;
}

interface MetaLeadAdsSectionProps {
  isVisible: boolean;
  onClose: () => void;
  searchParams?: URLSearchParams;
  setSearchParams?: (next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void;
}

export default function MetaLeadAdsSection({
  isVisible,
  onClose,
  searchParams,
  setSearchParams,
}: MetaLeadAdsSectionProps) {
  const [displayVerifyToken, setDisplayVerifyToken] = useState('');
  const [verifyTokenSet, setVerifyTokenSet] = useState(false);
  const [inputVerifyToken, setInputVerifyToken] = useState('');
  const [pages, setPages] = useState<MetaLeadPage[]>([]);
  const [connectAvailable, setConnectAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingToken, setSavingToken] = useState(false);
  const [addPageId, setAddPageId] = useState('');
  const [addPageName, setAddPageName] = useState('');
  const [addPageToken, setAddPageToken] = useState('');
  const [addingPage, setAddingPage] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [connectKey, setConnectKey] = useState<string | null>(null);
  const [connectPages, setConnectPages] = useState<Array<{ id: string; name: string }>>([]);
  const [connectLoading, setConnectLoading] = useState(false);
  const [selectingPageId, setSelectingPageId] = useState<string | null>(null);

  const load = async () => {
    if (!isVisible) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getMetaLeadsSettings();
      if (res.success && res.data) {
        setDisplayVerifyToken(res.data.verifyToken ?? '');
        setVerifyTokenSet(res.data.verifyTokenSet ?? false);
        setPages(res.data.pages ?? []);
        setConnectAvailable(res.data.connectAvailable ?? false);
      } else {
        setError(res.error ?? 'Failed to load settings');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) load();
  }, [isVisible]);

  const metaConnectKey = searchParams?.get('meta_connect') ?? null;

  useEffect(() => {
    if (!isVisible || !metaConnectKey || !setSearchParams) return;
    if (metaConnectKey === 'error') {
      setError('Facebook connect failed. Please try again.');
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('meta_connect');
        return p;
      });
      return;
    }
    setConnectKey(metaConnectKey);
    setConnectLoading(true);
    setError(null);
    adminApi
      .getMetaLeadsConnectPages(metaConnectKey)
      .then((r) => {
        if (r.success && r.data) {
          setConnectPages(r.data.pages);
        } else {
          setError(r.error ?? 'Could not load pages');
          clearConnectParam();
        }
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Could not load pages');
        clearConnectParam();
      })
      .finally(() => setConnectLoading(false));
  }, [isVisible, metaConnectKey, setSearchParams]);

  function clearConnectParam() {
    setConnectKey(null);
    setConnectPages([]);
    setSearchParams?.((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('meta_connect');
      return p;
    });
  }

  const startConnect = async () => {
    const res = await adminApi.getMetaLeadsConnectUrl();
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      setError('Connect is not configured (set META_APP_ID, META_APP_SECRET, API_PUBLIC_URL).');
    }
  };

  const selectConnectPage = async (pageId: string) => {
    if (!connectKey) return;
    setSelectingPageId(pageId);
    setError(null);
    try {
      const res = await adminApi.selectMetaLeadsConnectPage(connectKey, pageId);
      if (res.success && res.data) {
        setPages((prev) => [...prev, res.data!]);
        clearConnectParam();
      } else {
        setError(res.error ?? 'Failed to add page');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add page');
    } finally {
      setSelectingPageId(null);
    }
  };

  const saveVerifyToken = async () => {
    setSavingToken(true);
    setError(null);
    try {
      const value = inputVerifyToken.trim() || null;
      const res = await adminApi.putMetaLeadsSettings(value);
      if (res.success) {
        setDisplayVerifyToken(res.data?.verifyToken ?? '');
        setVerifyTokenSet(!!value);
        setInputVerifyToken('');
      } else {
        setError(res.error ?? 'Failed to save');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingToken(false);
    }
  };

  const addPage = async () => {
    if (!addPageId.trim() || !addPageToken.trim()) {
      setError('Page ID and access token are required');
      return;
    }
    setAddingPage(true);
    setError(null);
    try {
      const res = await adminApi.addMetaLeadsPage(
        addPageId.trim(),
        addPageToken.trim(),
        addPageName.trim() || undefined
      );
      if (res.success && res.data) {
        setPages((prev) => [...prev, res.data!]);
        setAddPageId('');
        setAddPageName('');
        setAddPageToken('');
      } else {
        setError(res.error ?? 'Failed to add page');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add page');
    } finally {
      setAddingPage(false);
    }
  };

  const removePage = async (id: string) => {
    setActioningId(id);
    setError(null);
    try {
      const res = await adminApi.deleteMetaLeadsPage(id);
      if (res.success) setPages((prev) => prev.filter((p) => p.id !== id));
      else setError(res.error ?? 'Failed to remove page');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setActioningId(null);
    }
  };

  const subscribe = async (id: string) => {
    setActioningId(id);
    setError(null);
    try {
      const res = await adminApi.subscribeMetaLeadsPage(id);
      if (res.success)
        setPages((prev) => prev.map((p) => (p.id === id ? { ...p, isSubscribed: true } : p)));
      else setError(res.error ?? 'Failed to subscribe');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to subscribe');
    } finally {
      setActioningId(null);
    }
  };

  const testPage = async (id: string) => {
    setActioningId(id);
    setError(null);
    try {
      const res = await adminApi.testMetaLeadsPage(id);
      if (res.success && res.data) {
        if (res.data.valid) {
          alert(`Token valid. Page name: ${res.data.name ?? '—'}`);
        } else {
          alert('Token is invalid or expired.');
        }
      } else {
        setError(res.error ?? 'Test failed');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setActioningId(null);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    alert('Webhook URL copied to clipboard.');
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Megaphone className="h-6 w-6 text-indigo-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Meta Lead Ads</h2>
              <p className="text-sm text-gray-600">
                Verify token, webhook URL, and connected pages
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Verify token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook verify token
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Use the same value in the Meta Developer Portal when subscribing the app to your
                  Page.
                </p>
                {verifyTokenSet && (
                  <p className="text-xs text-gray-600 mb-1">
                    Current: <span className="font-mono">{displayVerifyToken}</span>
                  </p>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputVerifyToken}
                    onChange={(e) => setInputVerifyToken(e.target.value)}
                    placeholder={
                      verifyTokenSet ? 'Enter new token to replace' : 'Enter verify token'
                    }
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    onClick={saveVerifyToken}
                    disabled={savingToken}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {savingToken ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save
                  </button>
                </div>
              </div>

              {/* Webhook URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL (read-only)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={webhookUrl}
                    className="flex-1 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600"
                  />
                  <button
                    type="button"
                    onClick={copyWebhookUrl}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                  >
                    <Copy className="h-4 w-4" /> Copy
                  </button>
                </div>
              </div>

              {/* Connected pages */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Connected pages</h3>
                <ul className="space-y-2">
                  {pages.length === 0 ? (
                    <li className="text-sm text-gray-500">
                      No pages connected yet. Add one below.
                    </li>
                  ) : (
                    pages.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div>
                          <span className="font-medium text-gray-900">
                            {p.pageName || p.metaPageId}
                          </span>
                          <span className="text-gray-500 text-sm ml-2">ID: {p.metaPageId}</span>
                          {p.isSubscribed ? (
                            <CheckCircle2
                              className="inline h-4 w-4 text-green-600 ml-1"
                              aria-label="Subscribed"
                            />
                          ) : (
                            <XCircle
                              className="inline h-4 w-4 text-amber-500 ml-1"
                              aria-label="Not subscribed"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => testPage(p.id)}
                            disabled={actioningId !== null}
                            className="p-1.5 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50"
                            title="Test token"
                          >
                            {actioningId === p.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </button>
                          {!p.isSubscribed && (
                            <button
                              onClick={() => subscribe(p.id)}
                              disabled={actioningId !== null}
                              className="text-sm text-indigo-600 hover:underline disabled:opacity-50"
                            >
                              Subscribe
                            </button>
                          )}
                          <button
                            onClick={() => removePage(p.id)}
                            disabled={actioningId !== null}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Remove page"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              {/* Add page */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Add page</h3>

                {connectAvailable && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={startConnect}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors"
                    >
                      <Link2 className="h-4 w-4" />
                      Connect with Facebook and select a page
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      Log in with Facebook and choose which Page to connect. No need to copy tokens.
                    </p>
                  </div>
                )}

                {connectKey && (connectLoading || connectPages.length >= 0) && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Select a page to add</h4>
                    {connectLoading ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading your pages…
                      </div>
                    ) : connectPages.length === 0 ? (
                      <p className="text-sm text-gray-600">
                        No Facebook Pages found for this account.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {connectPages.map((p) => (
                          <li
                            key={p.id}
                            className="flex items-center justify-between py-2 px-3 bg-white rounded border border-gray-200"
                          >
                            <span className="font-medium text-gray-900">{p.name}</span>
                            <span className="text-gray-500 text-sm">ID: {p.id}</span>
                            <button
                              type="button"
                              onClick={() => selectConnectPage(p.id)}
                              disabled={selectingPageId !== null}
                              className="text-indigo-600 hover:underline disabled:opacity-50 text-sm font-medium"
                            >
                              {selectingPageId === p.id ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="h-4 w-4 animate-spin" /> Adding…
                                </span>
                              ) : (
                                'Add this page'
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={clearConnectParam}
                      className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500 mb-2">
                  Or add manually: Page ID and Page access token from Meta (e.g. Graph API Explorer
                  or Business Settings).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={addPageId}
                    onChange={(e) => setAddPageId(e.target.value)}
                    placeholder="Page ID"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    value={addPageName}
                    onChange={(e) => setAddPageName(e.target.value)}
                    placeholder="Page name (optional)"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="password"
                    value={addPageToken}
                    onChange={(e) => setAddPageToken(e.target.value)}
                    placeholder="Page access token"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <button
                  onClick={addPage}
                  disabled={addingPage}
                  className="mt-2 flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-50"
                >
                  {addingPage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add page
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
